import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Swal from "sweetalert2";

import {
  fetchVendorBookings,
  updateBookingStatus,
  vendorUpdateBooking,
} from "../redux/slices/bookingSlice";

/* ================================
   TABS
================================ */
const TABS = ["Pending", "Approved", "Completed", "Cancelled"];

const TAB_STATUS_MAP = {
  Pending: ["pending", "submitted"],
  Approved: ["approved", "confirmed"],
  Completed: ["completed"],
  Cancelled: ["cancelled", "canceled"],
};

/* ================================
   STATUS BADGE
================================ */
const StatusBadge = ({ status }) => {
  const map = {
    confirmed: "success",
    completed: "success",
    cancelled: "danger",
    pending: "warning",
    approved: "primary",
  };

  return (
    <span className={`badge bg-${map[status] || "secondary"}`}>
      {status?.toUpperCase()}
    </span>
  );
};

/* ================================
   MAIN COMPONENT
================================ */
export default function VendorBookings() {
  const dispatch = useDispatch();
  const { bookings = [], loading } = useSelector((state) => state.booking);
  const { user } = useSelector((state) => state.auth);
  const vendorId = user?.vendor?._id;

  const [activeTab, setActiveTab] = useState("Pending");
  const [search, setSearch] = useState(""); // ✅ added

  /* ================================
     FETCH
  ================================ */
  useEffect(() => {
    if (vendorId) {
      dispatch(fetchVendorBookings(vendorId));
    }
  }, [dispatch, vendorId]);

  /* ================================
     ACTIONS
  ================================ */
  const approveBooking = async (id) => {
    const res = await Swal.fire({
      title: "Approve booking?",
      icon: "question",
      showCancelButton: true,
    });

    if (!res.isConfirmed) return;

    try {
      await dispatch(
        vendorUpdateBooking({ bookingId: id, action: "approve" })
      ).unwrap();

      Swal.fire("Approved", "Booking approved successfully", "success");
      dispatch(fetchVendorBookings(vendorId));
    } catch {
      Swal.fire("Error", "Approve failed", "error");
    }
  };

  const rejectBooking = async (id) => {
    const res = await Swal.fire({
      title: "Reject booking?",
      icon: "warning",
      showCancelButton: true,
    });

    if (!res.isConfirmed) return;

    try {
      await dispatch(
        vendorUpdateBooking({ bookingId: id, action: "reject" })
      ).unwrap();

      Swal.fire("Rejected", "Booking rejected", "success");
      dispatch(fetchVendorBookings(vendorId));
    } catch {
      Swal.fire("Error", "Reject failed", "error");
    }
  };

  const completeService = async (id) => {
    try {
      await dispatch(
        updateBookingStatus({ bookingId: id, status: "completed" })
      ).unwrap();

      Swal.fire("Completed", "Service marked completed", "success");
      dispatch(fetchVendorBookings(vendorId));
    } catch {
      Swal.fire("Error", "Update failed", "error");
    }
  };

  /* ================================
     FILTER + SEARCH + SORT
  ================================ */
  const filteredBookings = useMemo(() => {
    return bookings
      .filter((b) =>
        TAB_STATUS_MAP[activeTab].includes(b.status)
      )
      .filter((b) => {
        if (!search) return true;

        const keyword = search.toLowerCase();
        return (
          b.customerName?.toLowerCase().includes(keyword) ||
          b.bookingId?.toLowerCase().includes(keyword)
        );
      })
      .reverse(); // ✅ new entry first
  }, [bookings, activeTab, search]);

  return (
    <div className="container mt-4">
      {/* CARD */}
      <div className="card shadow-sm">
        <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
          <h6 className="mb-0 text-white">My Bookings</h6>

          {/* 🔍 SEARCH (no design change) */}
          <input
            type="text"
            className="form-control form-control-sm w-25"
            placeholder="Search name or booking id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="card-body">
          {/* TABS */}
          <ul className="nav nav-pills mb-3">
            {TABS.map((tab) => (
              <li className="nav-item" key={tab}>
                <button
                  className={`nav-link ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              </li>
            ))}
          </ul>

          {/* TABLE */}
          <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Booking ID</th>
                  <th>Customer</th>
                  <th>Date & Time</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th width="220">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredBookings.length === 0 && (
                  <tr className="text-small">
                    <td colSpan="6" className="text-center text-muted">
                      No bookings found
                    </td>
                  </tr>
                )}

                {filteredBookings.map((item) => (
                  <tr key={item._id}>
                    <td>#{item.bookingId}</td>
                    <td>{item.customerName}</td>
                    <td>
                      {moment(item.date).format("DD-MM-YYYY")} <br />
                      {moment(item.time, "HH:mm").format("hh:mm A")}
                    </td>
                    <td>
                      {item.address}
                      <br />
                      <small className="text-muted">
                        {item.city}, {item.state}
                      </small>
                    </td>
                    <td>
                      <StatusBadge status={item.status} />
                    </td>
                    <td>
                      {/* Pending */}
                      {TAB_STATUS_MAP.Pending.includes(item.status) && (
                        <>
                          <button
                            className="btn btn-success btn-sm me-2"
                            onClick={() => approveBooking(item._id)}
                          >
                            Approve
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => rejectBooking(item._id)}
                          >
                            Reject
                          </button>
                        </>
                      )}

                      {/* Approved */}
                      {TAB_STATUS_MAP.Approved.includes(item.status) && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => completeService(item._id)}
                        >
                          Service Complete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="text-center mt-3">Loading...</div>
          )}
        </div>
      </div>
    </div>
  );
}
